import { useState } from 'react'
import {
  Badge,
  Center,
  Container,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { IconCalendarEvent } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import type { TaskListItemDto } from '@ledokol/contracts'
import { fetchMyOpenTasks } from '../shared/api/tasks'
import { useAuthStore } from '../features/auth/store'
import { AppLayout } from '../features/projects/app-shell'
import { TaskModal } from '../features/task-modal/task-modal'

type BucketKey = 'overdue' | 'today' | 'week' | 'later' | 'no_due'

const BUCKETS: Array<{ key: BucketKey; label: string; color: string }> = [
  { key: 'overdue', label: 'Просрочено', color: 'red' },
  { key: 'today', label: 'Сегодня', color: 'orange' },
  { key: 'week', label: 'На неделе', color: 'yellow' },
  { key: 'later', label: 'Позже', color: 'blue' },
  { key: 'no_due', label: 'Без срока', color: 'gray' },
]

function bucketOf(t: TaskListItemDto): BucketKey {
  if (!t.due_at) return 'no_due'
  if (t.due_state === 'overdue') return 'overdue'
  const due = dayjs(t.due_at)
  if (due.isSame(dayjs(), 'day')) return 'today'
  if (due.isBefore(dayjs().add(7, 'day'))) return 'week'
  return 'later'
}

export function MyWorkPage() {
  const { user } = useAuthStore()
  const [openTaskId, setOpenTaskId] = useState<string | null>(null)

  const q = useQuery({
    queryKey: ['my-tasks', user?.id],
    queryFn: () => fetchMyOpenTasks(user!.id),
    enabled: !!user,
  })

  const groups = BUCKETS.map((b) => ({
    ...b,
    tasks: (q.data ?? []).filter((t) => bucketOf(t) === b.key),
  }))

  return (
    <AppLayout>
      <Container size="lg" py="md">
        <Group justify="space-between" mb="md">
          <div>
            <Text size="xs" c="blue.7" fw={700} tt="uppercase">
              Задачи
            </Text>
            <Title order={2}>Моя работа</Title>
          </div>
          {q.data && (
            <Text size="sm" c="dimmed">
              Открытых задач: {q.data.length}
            </Text>
          )}
        </Group>

        {q.isLoading && (
          <Center mih={200}>
            <Loader />
          </Center>
        )}
        {q.isError && (
          <Center mih={200}>
            <Text c="red">Не удалось загрузить задачи</Text>
          </Center>
        )}

        {q.data && q.data.length === 0 && (
          <Center mih={200}>
            <Text c="dimmed">Открытых задач нет — всё сделано 🎉</Text>
          </Center>
        )}

        {q.data && q.data.length > 0 && (
          <Stack gap="lg">
            {groups
              .filter((g) => g.tasks.length > 0)
              .map((g) => (
                <Stack key={g.key} gap="xs">
                  <Group gap="xs">
                    <Badge color={g.color} variant="light" size="lg">
                      {g.label} · {g.tasks.length}
                    </Badge>
                  </Group>
                  {g.tasks.map((t) => (
                    <TaskRow key={t.id} task={t} onOpen={() => setOpenTaskId(t.id)} />
                  ))}
                </Stack>
              ))}
          </Stack>
        )}
      </Container>

      <TaskModal taskId={openTaskId} onClose={() => setOpenTaskId(null)} onOpenTask={setOpenTaskId} />
    </AppLayout>
  )
}

function TaskRow({ task, onOpen }: { task: TaskListItemDto; onOpen: () => void }) {
  return (
    <Paper
      withBorder
      p="sm"
      radius="md"
      onClick={onOpen}
      style={{ cursor: 'pointer', borderLeft: `3px solid ${task.project.accent_color}` }}
    >
      <Group justify="space-between" wrap="nowrap">
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Text size="sm" fw={600} truncate>
            <Text span c="dimmed" fw={600} mr={4}>#{task.human_id}</Text>
            {task.title}
          </Text>
          <Text size="xs" c="dimmed">
            {task.project.name}
          </Text>
        </Stack>
        <Group gap="xs" wrap="nowrap">
          {task.due_at && (
            <Badge
              color={dueColor(task.due_state)}
              variant="light"
              leftSection={<IconCalendarEvent size={11} />}
            >
              {dayjs(task.due_at).format('D MMM HH:mm')}
            </Badge>
          )}
          <Badge color={priorityColor(task.priority)} variant="dot">
            {priorityLabel(task.priority)}
          </Badge>
        </Group>
      </Group>
    </Paper>
  )
}

function priorityColor(p: string): string {
  return { low: 'gray', med: 'blue', high: 'orange', urgent: 'red' }[p] ?? 'gray'
}
function priorityLabel(p: string): string {
  return { low: 'Низкий', med: 'Средний', high: 'Высокий', urgent: 'Срочный' }[p] ?? p
}
function dueColor(s: string): string {
  return { none: 'gray', ok: 'green', soon: 'yellow', overdue: 'red' }[s] ?? 'gray'
}
